// src/services/doctorService.js
import apiClient from "./apiClient";
import axios from "axios";
import { BACKEND_URL, USERMGMT_URL, getEnvVar } from "../utils/envConfig";
import userManagementApi from "./userManagementApi";
import { getAllHospitalOrganizations } from "./hospitalOrganizationService";
import subscriptionRoleLimitsService from "./subscriptionRoleLimitsService";
const API_URL = `/doctors`; // Use relative path since apiClient has baseURL
const USER_MGMT_URL = USERMGMT_URL;

// Maximum organizationId to try when searching for a user (fallback if we can't get list from backend)
// Can be overridden via environment variable REACT_APP_MAX_ORG_ID
// Default is 1000, but can be increased if you have more organizations
const MAX_ORGANIZATION_ID = parseInt(getEnvVar("REACT_APP_MAX_ORG_ID", "1000"), 10);
  
// Simple in-memory cache for getAllDoctors to reduce load and UI hangs
let __allDoctorsCache = { data: null, timestamp: 0, inFlight: null, cacheKey: null };
const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const invalidateDoctorsCache = () => {
  __allDoctorsCache = { data: null, timestamp: 0, inFlight: null, cacheKey: null };
};

export const getDoctorsByOrganizationId = async (organizationId) => {
  if (!organizationId) {
    return [];
  }

  try {
    const response = await apiClient.get(API_URL, {
      params: {
        organizationId: Number(organizationId),
      },
    });

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error(
      `[doctorService] Error fetching doctors for organization ${organizationId}:`,
      error
    );

    return [];
  }
};

export const getAllDoctors = async (options = {}) => {
  const { forceRefresh = false } = options;
  const now = Date.now();
  
  // Get organizationId from localStorage for cache key
  // Only use if it's a valid positive number (common users won't have it)
  const storedUser = localStorage.getItem("user");
  let organizationId = null;
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      const orgId = user?.organizationId;
      // Only use organizationId if it's a valid positive number
      if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
        organizationId = Number(orgId);
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  // Create cache key that includes organizationId (or 'all' for common users)
  const cacheKey = organizationId && organizationId > 0 ? `org_${organizationId}` : 'all';
  
  if (forceRefresh) {
    invalidateDoctorsCache();
  } else if (
    __allDoctorsCache.data &&
    __allDoctorsCache.cacheKey === cacheKey &&
    now - __allDoctorsCache.timestamp < FIVE_MINUTES_MS
  ) {
    return __allDoctorsCache.data;
  }
  if (__allDoctorsCache.inFlight && __allDoctorsCache.cacheKey === cacheKey) {
    return __allDoctorsCache.inFlight;
  }
  
  // apiClient interceptor will automatically add organizationId as query param
  // If user has no organizationId, backend should use base table
  __allDoctorsCache.inFlight = apiClient.get(API_URL)
    .then(async (response) => {
      let data = response.data;
      
      // Return data as-is - backend should handle filtering via organizationId query param
      // Client-side filtering removed as it was causing issues with valid organization data
      __allDoctorsCache.data = data;
      __allDoctorsCache.timestamp = Date.now();
      __allDoctorsCache.cacheKey = cacheKey;
      return data;
    })
    .catch((error) => {
      // Handle case where organization-specific table doesn't exist yet or backend error
      if (error.response?.status === 500 && 
          (error.response?.data?.message?.includes("Failed to retrieve") ||
           error.response?.data?.message?.includes("from table") ||
           error.response?.data?.message?.includes("does not exist"))) {
        console.warn("Organization-specific table not found, returning empty array:", error.response.data.message);
        const emptyData = [];
        __allDoctorsCache.data = emptyData;
        __allDoctorsCache.timestamp = Date.now();
        __allDoctorsCache.cacheKey = cacheKey;
        return emptyData;
      }
      console.error("Error fetching doctors:", error);
      throw error;
    })
    .finally(() => {
      __allDoctorsCache.inFlight = null;
    });
  return __allDoctorsCache.inFlight;
};

export const getDoctorById = async (id, organizationId) => {
  const config = { timeout: 10000 };
  if (organizationId != null && organizationId !== "") {
    config.params = { organizationId: Number(organizationId) };
  }
  const response = await apiClient.get(`${API_URL}/${id}`, config);
  return response.data;
};

export const getDoctorByMobile = async (mobile, organizationId) => {
  if (!mobile || !organizationId) {
    throw new Error("Mobile number and organizationId are required");
  }
  
  try {
    console.log(`[doctorService] Searching doctor by mobile ${mobile} in organization ${organizationId}`);
    const response = await apiClient.get(`${API_URL}/by-mobile`, {
      params: { 
        mobile: mobile.trim(),
        organizationId: Number(organizationId)
      }
    });
    
    const data = response.data;
    console.log(`[doctorService] ✅ Doctor found by mobile ${mobile} in organization ${organizationId}`);
    
    // Verify the response has matching organizationId
    const orgIdFromResponse = data?.organizationId || data?.organization_id;
    if (orgIdFromResponse && Number(orgIdFromResponse) === Number(organizationId)) {
      return data;
    }
    
    // If organizationId doesn't match, this is an error
    const error = new Error(`Doctor found in organization ${orgIdFromResponse}, but searched for organization ${organizationId}`);
    error.response = { status: 403, data: { message: `This user does not belong to the selected hospital organization` } };
    throw error;
  } catch (error) {
    console.error(`[doctorService] Error searching doctor by mobile ${mobile} in organization ${organizationId}:`, error);
    throw error;
  }
};

export const getDoctorByUserId = async (userId, mobile = null) => {
  console.log(`[doctorService] getDoctorByUserId called for userId: ${userId}, mobile: ${mobile}`);
  
  // Get organizationId from localStorage (set by login flow with selectedOrgId)
  // During login, we set organizationId to the selected organization before calling this function
  const storedUser = localStorage.getItem("user");
  const storedOrgId = localStorage.getItem("organizationId");
  let organizationId = null;
  
  // Prefer organizationId from localStorage (set by login with selectedOrgId)
  if (storedOrgId) {
    try {
      organizationId = Number(storedOrgId);
      if (!isNaN(organizationId) && organizationId > 0) {
        console.log(`[doctorService] Using organizationId ${organizationId} from localStorage (selected during login)`);
      } else {
        organizationId = null;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  // If no organizationId in localStorage, try to get from user object
  if (!organizationId && storedUser) {
    try {
      const user = JSON.parse(storedUser);
      const userOrgId = user?.organizationId;
      if (userOrgId != null && userOrgId !== "" && !isNaN(userOrgId) && Number(userOrgId) > 0) {
        organizationId = Number(userOrgId);
        console.log(`[doctorService] Using organizationId ${organizationId} from user object`);
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  // When organizationId is selected, use mobile search first (more reliable than userId)
  // The login API may return wrong userId, but mobile number is consistent
  if (organizationId && mobile) {
    try {
      return await getDoctorByMobile(mobile, organizationId);
    } catch (error) {
      // If mobile search fails, fall through to userId search
      console.warn(`[doctorService] Mobile search failed, will try userId search:`, error.message);
    }
  }
  
  // If we have organizationId (but no mobile or mobile search failed), try to fetch doctor from that organization first
  // If it fails (user doesn't exist in that org), try without organizationId to search across all orgs
  if (organizationId) {
    try {
      console.log(`[doctorService] Fetching doctor for userId ${userId} in organization ${organizationId}`);
      const params = { organizationId: organizationId };
      // If mobile is provided, include it as a parameter (backend will try mobile first, then userId)
      if (mobile) {
        params.mobile = mobile.trim();
      }
      
      const response = await apiClient.get(`${API_URL}/user/${userId}`, { params });
      const data = response.data;
      
      // Verify the response has matching organizationId
      const orgIdFromResponse = data?.organizationId || data?.organization_id;
      if (orgIdFromResponse && Number(orgIdFromResponse) === organizationId) {
        console.log(`[doctorService] ✅ Doctor found for userId ${userId} in organization ${organizationId}`);
        return data;
      }
      
      // If organizationId doesn't match, this is an error
      console.warn(`[doctorService] ⚠️ OrganizationId mismatch: expected ${organizationId}, got ${orgIdFromResponse}`);
      // Fall through to search without organizationId
    } catch (error) {
      console.warn(`[doctorService] Doctor not found for userId ${userId} in organization ${organizationId}, will search across all organizations`);
      // Fall through to search without organizationId
      organizationId = null; // Clear organizationId to search across all orgs
    }
  }
  
  // Try without organizationId (backend should search across all organization tables)
  // Save the selected organizationId before clearing it
  const tempOrgId = localStorage.getItem("organizationId");
  
  // Use axios directly (bypassing apiClient interceptor) to search without organizationId
  // This ensures the backend can search across all organization tables
  try {
    console.log(`[doctorService] Calling /doctors/user/${userId} without organizationId (searching across all organizations)...`);
    // Use axios directly with the base URL to bypass apiClient interceptor
    const token = localStorage.getItem('token');
    const response = await axios.get(`${BACKEND_URL}${API_URL}/user/${userId}`, {
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });
    const data = response.data;
    
    console.log(`[doctorService] ✅ Doctor found for userId ${userId}`);
    console.log(`[doctorService] Full response data:`, data);
    
    // Check for organizationId in response
    const orgIdFromResponse = data?.organizationId || data?.organization_id;
    if (orgIdFromResponse != null && orgIdFromResponse !== "" && !isNaN(orgIdFromResponse) && Number(orgIdFromResponse) > 0) {
      const foundOrgId = Number(orgIdFromResponse);
      console.log(`[doctorService] ✅ Doctor found with organizationId: ${foundOrgId}`);
      
      // If we had a selected organizationId, validate that it matches
      const storedOrgId = localStorage.getItem("organizationId");
      if (storedOrgId && Number(storedOrgId) !== foundOrgId) {
        console.warn(`[doctorService] ⚠️ Found doctor in organization ${foundOrgId}, but selected organization is ${storedOrgId}`);
        // This means the user exists but in a different organization - this is an error
        const error = new Error(`Doctor found in organization ${foundOrgId}, but login was attempted for organization ${storedOrgId}`);
        error.response = { status: 403, data: { message: `This user does not belong to the selected hospital organization` } };
        throw error;
      }
      
      // Update localStorage with organizationId from response
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const updatedUser = { ...user, organizationId: foundOrgId };
          localStorage.setItem("user", JSON.stringify(updatedUser));
          localStorage.setItem("organizationId", foundOrgId.toString());
          console.log(`[doctorService] ✅ Updated localStorage with organizationId: ${foundOrgId}`);
        } catch (e) {
          console.error("Error updating localStorage:", e);
        }
      }
    }
    
    return data;
  } catch (error) {
    // If backend returns 500/404 OR network error (CORS), try with common organizationIds
    // Network errors don't have error.response, so we check for that too
    const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');
    const isServerError = error.response?.status === 500 || error.response?.status === 404;
    
    if (isServerError || isNetworkError) {
      console.log(`[doctorService] Backend requires organizationId or network error occurred.`);
      console.log(`[doctorService] Error type: ${isNetworkError ? 'Network/CORS' : 'Server Error'}, Status: ${error.response?.status}`);
      
      // First, try to get list of organizations from backend
      let organizationIdsToTry = [];
      try {
        console.log(`[doctorService] Attempting to get list of organizations from backend...`);
        const organizations = await getAllHospitalOrganizations();
        if (Array.isArray(organizations) && organizations.length > 0) {
          organizationIdsToTry = organizations
            .map(org => org.id || org.organizationId || org.organization_id)
            .filter(id => id != null && !isNaN(id) && Number(id) > 0)
            .map(id => Number(id));
          console.log(`[doctorService] ✅ Got ${organizationIdsToTry.length} organizations from backend:`, organizationIdsToTry);
        }
      } catch (orgListError) {
        console.warn(`[doctorService] Could not get organization list from backend, will use fallback range 1-${MAX_ORGANIZATION_ID}`);
      }
      
      // If we couldn't get the list, use fallback range
      if (organizationIdsToTry.length === 0) {
        console.log(`[doctorService] Using fallback range: organizationIds 1-${MAX_ORGANIZATION_ID}`);
        console.log(`[doctorService] Note: If your organization ID is > ${MAX_ORGANIZATION_ID}, set REACT_APP_MAX_ORG_ID environment variable`);
        for (let i = 1; i <= MAX_ORGANIZATION_ID; i++) {
          organizationIdsToTry.push(i);
        }
      }
      
      // Try each organizationId
      for (const orgId of organizationIdsToTry) {
        try {
          console.log(`[doctorService] Trying with organizationId: ${orgId}...`);
          const response = await apiClient.get(`${API_URL}/user/${userId}`, {
            params: { organizationId: orgId }
          });
          
          if (response.data) {
            const data = response.data;
            console.log(`[doctorService] ✅✅✅ FOUND doctor for userId ${userId} in organization ${orgId}!!!`);
            
            // Update localStorage
            if (storedUser) {
              try {
                const user = JSON.parse(storedUser);
                const updatedUser = { ...user, organizationId: orgId };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                localStorage.setItem("organizationId", orgId.toString());
                console.log(`[doctorService] ✅✅✅ UPDATED localStorage with organizationId: ${orgId}`);
                console.log(`[doctorService] Updated user object:`, updatedUser);
              } catch (e) {
                console.error("Error updating localStorage:", e);
              }
            }
            
            return data;
          }
        } catch (orgError) {
          // Continue to next organizationId (silently for 404/500/network errors)
          const isOrgNetworkError = !orgError.response && (orgError.code === 'ERR_NETWORK' || orgError.message === 'Network Error');
          if (orgError.response?.status !== 404 && orgError.response?.status !== 500 && !isOrgNetworkError) {
            console.warn(`[doctorService] Error checking organization ${orgId}:`, orgError.message);
          }
        }
      }
    }
    
    // If all attempts failed, throw the original error
    console.error(`[doctorService] ❌ Doctor not found for userId ${userId} after trying all methods`);
    console.error(`[doctorService] Original error:`, error.message || error);
    throw error;
  }
};

export const getDoctorByDepartmentId = async (departmentId) => {
  const response = await apiClient.get(`${API_URL}/department/${departmentId}`);
  return response.data;
};

export const getDoctorsByDepartmentIdMultiple = async (departmentId, options = {}) => {
  // apiClient will automatically add organizationId as query param via interceptor
  // The request will be: GET /api/doctors/by-department-multiple/{departmentId}?organizationId={orgId}
  console.log(`[doctorService] Fetching doctors for department ${departmentId}`);
  try {
    const organizationId = Number(options.organizationId);
    const response = await apiClient.get(
      `${API_URL}/by-department-multiple/${departmentId}`,
      Number.isFinite(organizationId) && organizationId > 0
        ? { params: { organizationId } }
        : undefined
    );
    console.log(`[doctorService] API Response Status: ${response.status}`);
    console.log(`[doctorService] Received ${response.data?.length || 0} doctors from API`);
    if (response.data && response.data.length > 0) {
      console.log(`[doctorService] Sample doctor:`, response.data[0]);
    }
    return response.data || [];
  } catch (error) {
    console.error(`[doctorService] Error fetching doctors for department ${departmentId}:`, error);
    console.error(`[doctorService] Error response:`, error.response?.data);
    // Return empty array on error to prevent crashes
    return [];
  }
};

// export const createDoctor = async (doctor) => {
//   const userData = {
//     fullName: doctor.name,
//     password: "Doctor@123",
//     role: doctor.userRole,
//     email: doctor.email,
//     department: doctor.departmentName || "",
//     mobile: doctor.mobile,
//   };
//   const userResponse = await fetch(`${USER_MGMT_URL}/register`, {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify(userData),
//   });
//   console.log('doctor Register', userResponse); return false; 
//   if (!userResponse.ok) {
//     throw new Error('User registration failed');
//   }
//   const createdUser = await userResponse.json();
//   doctor.userId = createdUser.id;
//   const response = await axios.post(API_URL, doctor);
//   if (response.status !== 200) {
//     throw new Error('Doctor creation failed');
//   }
//   return response.data;
// };
export const createDoctor = async (doctor) => {
  // Get organizationId from localStorage for user registration
  // Only include if it's a valid positive number (common users won't have it)
  const storedUser = localStorage.getItem("user");
  let organizationId = null;
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      const orgId = user?.organizationId;
      // Only use organizationId if it's a valid positive number
      if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
        organizationId = Number(orgId);
      }
    } catch (e) {
      console.error("Error parsing stored user:", e);
    }
  }
  
  // Check subscription role limits before creating doctor
  // For doctors, check both "doctor" and "surgeon" roles as they might use either
  const roleToCheck = doctor.userRole?.toLowerCase() || "doctor";
  if (organizationId) {
    try {
      const limitCheck = await subscriptionRoleLimitsService.checkRoleLimit(
        organizationId,
        roleToCheck
      );
      
      if (!limitCheck.allowed) {
        const error = new Error(limitCheck.message);
        error.limitCheck = limitCheck;
        throw error;
      }
    } catch (error) {
      // If it's a limit check error, re-throw it
      if (error.limitCheck) {
        throw error;
      }
      // For other errors (e.g., API errors), log but allow creation to proceed
      // This ensures the system doesn't break if the subscription service is down
      console.warn("Error checking subscription role limits, allowing creation to proceed:", error);
    }
  }
  
  const userData = { 
    fullName: doctor.name,
    password: doctor.password,
    role: doctor.userRole,
    email: doctor.email,
    department: doctor.departmentName || "",
    mobile: doctor.mobile,
    ...(organizationId && { organizationId }), // Include organizationId if available
  };
  const userResponse = await fetch(`${USER_MGMT_URL}/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });

  let createdUser = null;
  const responseText = await userResponse.text();
  if (responseText) {
    try {
      createdUser = JSON.parse(responseText);
    } catch (e) {
      console.error("[doctorService] Failed to parse register response:", responseText);
    }
  }

  if (!userResponse.ok) {
    const errorMsg = createdUser?.message || createdUser?.error || 'User registration failed';
    const err = new Error(errorMsg);
    err.response = { status: userResponse.status, data: createdUser || { message: errorMsg } };
    throw err;
  }

  if (!createdUser || !createdUser.id) {
    throw new Error('User with this mobile number may already exist. Please check and try again.');
  }

  doctor.userId = createdUser.id;
  
  // Workaround: Update organization_id if it's null in the response
  // This happens when the user management backend doesn't store organization_id during registration
  // Similar to how admin user creation works in onboarding
  if ((createdUser.organization_id === null || createdUser.organization_id === undefined) && organizationId) {
    try {
      // Use snake_case field names to match the updateUser endpoint format
      const updatePayload = {
        id: createdUser.id,
        full_name: doctor.name,
        user_pwd: doctor.password, // Include password (required field)
        user_role: doctor.userRole,
        email: doctor.email,
        department: doctor.departmentName || "",
        mobile: doctor.mobile,
        organization_id: organizationId, // Include organization_id in update payload
      };
      
      const updateResponse = await userManagementApi.post(
        "/updateUser",
        updatePayload,
      );
      
      if (updateResponse.status < 200 || updateResponse.status >= 300) {
        console.warn("Failed to update organization_id for user:", createdUser.id);
      } else {
        console.log("Updated organization_id for user:", createdUser.id);
      }
    } catch (updateError) {
      console.warn("Error updating organization_id:", updateError);
      // Don't fail the whole operation if organization_id update fails
    }
  }
  
  // apiClient will automatically add organizationId via interceptor
  const response = await apiClient.post(`${API_URL}`, doctor);
  invalidateDoctorsCache();
  return response.data;
};

export const updateDoctor = async (id, userId, doctor) => {
  // Get organizationId for logging
  const storedUser = localStorage.getItem("user");
  let organizationId = null;
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      organizationId = user?.organizationId || localStorage.getItem("organizationId");
      if (organizationId && typeof organizationId === 'string') {
        organizationId = parseInt(organizationId, 10);
      }
    } catch (e) {
      // ignore
    }
  }
  
  console.log(`[doctorService] updateDoctor: id=${id}, userId=${userId}, organizationId=${organizationId}`);
  console.log(`[doctorService] Doctor data:`, {
    ...doctor,
    visitingSchedulesCount: doctor.visitingSchedules?.length || 0,
    visitingSchedules: doctor.visitingSchedules
  });
  console.log(`[doctorService] apiClient will automatically add organizationId to request body`);
  
  const response = await apiClient.put(`${API_URL}/${id}`, doctor);
  invalidateDoctorsCache();
  if (response.status === 200) {
    // Get organizationId from response or localStorage
    let orgId = organizationId;
    if (!orgId) {
      const orgIdFromResponse = response.data?.organizationId || response.data?.organization_id;
      if (orgIdFromResponse != null && orgIdFromResponse !== "" && !isNaN(orgIdFromResponse)) {
        orgId = Number(orgIdFromResponse);
      }
    }
    
    const data = {  
      id:userId,        
      full_name: response.data.name,
      user_role: response.data.userRole,
      email: response.data.email,
      department: response.data.departmentName || "",
      mobile: response.data.mobile,
      // Include organization_id if available (similar to admin user updates)
      ...(orgId && { organization_id: orgId }),
    };
    
    // Only include password in user management update if it was provided in the doctor update request
    // This ensures we only update the password when the user/admin explicitly wants to change it
    if (doctor.password && doctor.password.trim()) {
      data.user_pwd = doctor.password.trim();
      console.log("[doctorService] Password provided in update request, will update user management password");
    } else {
      console.log("[doctorService] Password not provided in update request, will not update user management password");
    }
    
    try {
      await userManagementApi.post(
        "/updateUser",
        data
      );
    } catch (userError) {
      console.warn(
        "Error updating user in user management service:",
        userError
      );
    
      // Don't fail doctor update if User Management update fails
    }
  }
  return response.data;
};

export const deleteDoctor = async (id) => {
  const response = await apiClient.delete(`${API_URL}/${id}`);
  invalidateDoctorsCache();
  return response.data;
};

/**
 * Set doctor active/inactive in the org-specific {organizationId}_doctor table only.
 * Login is blocked via HMS (is_active on doctor row), not user management.
 */
export const setDoctorActiveStatus = async (doctorId, userId, isActive) => {
  const body = {
    isActive,
    is_active: isActive ? 1 : 0,
    ...(userId != null ? { userId } : {}),
  };

  let doctorResult = null;

  try {
    const response = await apiClient.put(`${API_URL}/${doctorId}/status`, body);
    doctorResult = response.data;
  } catch (statusError) {
    if (statusError.response?.status === 404) {
      const response = await apiClient.put(`${API_URL}/${doctorId}`, body);
      doctorResult = response.data;
    } else {
      throw statusError;
    }
  }

  invalidateDoctorsCache();
  return doctorResult;
};

export const DEACTIVATED_DOCTOR_LOGIN_MSG =
  "This doctor account is deactivated. Contact your hospital administrator.";

/** True when HMS reports this doctor row is deactivated (is_active = 0). */
export const isDoctorDeactivated = (doctor) => {
  if (!doctor) return false;
  const activeStatus = doctor.isActive ?? doctor.is_active;
  return activeStatus === 0 || activeStatus === false;
};

/**
 * Optional post-login check: HMS org doctor table only.
 * GET /api/doctors/validate-login
 */
export const validateDoctorLogin = async (organizationId, userId, mobile = null) => {
  const params = {
    organizationId,
    userId,
    ...(mobile ? { mobile: String(mobile).trim() } : {}),
  };
  const response = await apiClient.get(`${API_URL}/validate-login`, { params });
  return response.data;
};

export const isDoctorDeactivatedLoginError = (error) => {
  const status = error?.response?.status;
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "";
  return (
    error?.code === "DOCTOR_DEACTIVATED" ||
    (status === 403 && /deactivat/i.test(String(message)))
  );
};

const throwDoctorDeactivated = (message) => {
  const err = new Error(message || DEACTIVATED_DOCTOR_LOGIN_MSG);
  err.code = "DOCTOR_DEACTIVATED";
  throw err;
};

/**
 * Post-auth, pre-navigation: ensure HMS org doctor row allows login.
 * Sets organizationId in localStorage for apiClient. Throws with code DOCTOR_DEACTIVATED.
 */
export const assertDoctorCanLogin = async (organizationId, userId, mobile) => {
  if (organizationId != null) {
    localStorage.setItem("organizationId", String(organizationId));
  }

  let doctorRow = null;
  if (mobile && organizationId) {
    try {
      doctorRow = await getDoctorByMobile(mobile, organizationId);
    } catch (e) {
      if (isDoctorDeactivatedLoginError(e)) {
        throwDoctorDeactivated(e.response?.data?.message);
      }
    }
  }

  if (!doctorRow && userId) {
    try {
      doctorRow = await getDoctorByUserId(userId, mobile || null);
    } catch (e) {
      if (isDoctorDeactivatedLoginError(e)) {
        throwDoctorDeactivated(e.response?.data?.message);
      }
    }
  }

  if (isDoctorDeactivated(doctorRow)) {
    throwDoctorDeactivated();
  }

  try {
    const validation = await validateDoctorLogin(organizationId, userId, mobile);
    if (validation?.allowed === false || validation?.active === false) {
      throwDoctorDeactivated(validation?.message);
    }
  } catch (e) {
    if (e?.code === "DOCTOR_DEACTIVATED") throw e;
    if (isDoctorDeactivatedLoginError(e)) {
      throwDoctorDeactivated(e.response?.data?.message);
    }
  }

  return doctorRow;
};

export const deactivateDoctor = async (doctorId, userId) =>
  setDoctorActiveStatus(doctorId, userId, false);

export const activateDoctor = async (doctorId, userId) =>
  setDoctorActiveStatus(doctorId, userId, true);
